import { useState } from 'react'
import { Icon } from '@iconify/react'
import { buildMyAffiliateLink } from '@/lib/my-affiliate'
import type { Affiliate, AffiliateAllowedCampaign } from '@/types/affiliates'
import styles from './AffiliateLinksModal.module.css'

interface AffiliateLinksModalProps {
  affiliate: Affiliate
  onClose: () => void
}

export function AffiliateLinksModal({ affiliate, onClose }: AffiliateLinksModalProps) {
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)

  const campaigns = affiliate.allowed_campaigns ?? []

  const copyLink = (campaign: AffiliateAllowedCampaign) => {
    const url = buildMyAffiliateLink(campaign, affiliate.tracking_code)
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(String(campaign.id))
      setTimeout(() => setCopiedSlug(null), 2000)
    })
  }

  const copyAll = () => {
    const text = campaigns
      .map((c) => {
        const label = c.title?.trim() || c.name
        return `${label}: ${buildMyAffiliateLink(c, affiliate.tracking_code)}`
      })
      .join('\n')
    void navigator.clipboard.writeText(text)
    setCopiedSlug('__all__')
    setTimeout(() => setCopiedSlug(null), 2000)
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="affiliate-links-title">
      <div className={styles.modal}>
        <div className={styles.header}>
          <div>
            <h3 id="affiliate-links-title" className={styles.title}>
              Links de indicação
            </h3>
            <p className={styles.subtitle}>{affiliate.affiliate_name}</p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            <Icon icon="mdi:close" />
          </button>
        </div>

        <div className={styles.body}>
          {campaigns.length === 0 ? (
            <p className={styles.empty}>Nenhuma campanha ativa disponível para gerar links.</p>
          ) : (
            <ul className={styles.list}>
              {campaigns.map((campaign) => {
                const url = buildMyAffiliateLink(campaign, affiliate.tracking_code)
                const label = campaign.title?.trim() || campaign.name
                const copied = copiedSlug === String(campaign.id)
                return (
                  <li key={campaign.id} className={styles.item}>
                    <div className={styles.itemHead}>
                      <span className={styles.campaignName}>
                        {label}
                        {campaign.commission_percent !== undefined ? (
                          <span className={styles.campaignCommission}>
                            {' '}
                            · {campaign.commission_percent}%
                          </span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className={`${styles.copyBtn} ${copied ? styles.copied : ''}`}
                        onClick={() => copyLink(campaign)}
                        title="Copiar link"
                      >
                        <Icon icon={copied ? 'mdi:check' : 'mdi:content-copy'} />
                        Copiar
                      </button>
                    </div>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                      title={url}
                    >
                      {url}
                    </a>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className={styles.footer}>
          {campaigns.length > 1 ? (
            <button
              type="button"
              className={styles.copyAllBtn}
              onClick={copyAll}
              disabled={campaigns.length === 0}
            >
              <Icon icon={copiedSlug === '__all__' ? 'mdi:check' : 'mdi:content-copy'} />
              {copiedSlug === '__all__' ? 'Copiado' : 'Copiar todos'}
            </button>
          ) : null}
          <button type="button" className={styles.closeFooterBtn} onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
